/**
 * Settings API Client
 *
 * Low-level client for new Settings API
 * Provides thin wrapper around HTTP endpoints
 */

import api from './api';

/**
 * Retry a failed API call with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxRetries - Maximum retry attempts
 * @param {number} baseDelay - Base delay in milliseconds
 * @returns {Promise} Result of function call
 */
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      // Don't retry on 4xx errors (client errors)
      if (error.response?.status >= 400 && error.response?.status < 500) {
        throw error;
      }

      // Last attempt, throw the error
      if (attempt === maxRetries) {
        console.error(`[Settings API] Max retries (${maxRetries}) exceeded`);
        throw error;
      }

      // Wait with exponential backoff
      const delay = baseDelay * Math.pow(2, attempt);
      console.warn(
        `[Settings API] Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`,
        error.message
      );
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

/**
 * Settings API Client Class
 */
class SettingsApiClient {
  constructor(baseURL = null) {
    this.baseURL = baseURL ||
      process.env.REACT_APP_SETTINGS_API_URL ||
      process.env.REACT_APP_API_URL ||
      '';
  }

  /**
   * Get base URL for Settings API
   * @returns {string} Base URL
   */
  getBaseURL() {
    return this.baseURL;
  }

  /**
   * Build full URL for endpoint
   * @param {string} endpoint - API endpoint path
   * @returns {string} Full URL
   */
  buildURL(endpoint) {
    // If using axios instance, it handles baseURL
    // Otherwise, construct full URL
    return endpoint.startsWith('http') ? endpoint : endpoint;
  }

  /**
   * Get all settings grouped by section
   * @returns {Promise<Object>} { section: [...settings] }
   */
  async getSettingsGrouped() {
    return retryWithBackoff(async () => {
      const response = await api.get('/api/settings/grouped');
      return response.data;
    });
  }

  /**
   * Get all settings for a specific section
   * @param {string} section - Section name (e.g., 'payment')
   * @returns {Promise<Array>} Array of setting objects
   */
  async getSettingsBySection(section) {
    if (!section) {
      throw new Error('Section is required');
    }

    return retryWithBackoff(async () => {
      const response = await api.get(`/api/settings/section/${section}`);
      return response.data;
    });
  }

  /**
   * Get a specific setting
   * @param {string} section - Section name
   * @param {string} key - Setting key
   * @returns {Promise<Object|null>} Setting object or null if not found
   */
  async getSetting(section, key) {
    if (!section || !key) {
      throw new Error('Section and key are required');
    }

    try {
      const response = await api.get(`/api/settings/${section}/${key}`);
      return response.data;
    } catch (error) {
      // Return null for 404, throw for other errors
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Update or create a setting (upsert)
   * @param {string} section - Section name
   * @param {string} key - Setting key
   * @param {string|number|boolean} value - Setting value
   * @returns {Promise<Object>} Updated setting object
   */
  async updateSetting(section, key, value) {
    if (!section || !key) {
      throw new Error('Section and key are required');
    }

    if (value === undefined || value === null) {
      throw new Error('Value is required');
    }

    return retryWithBackoff(async () => {
      const response = await api.put(
        `/api/settings/${section}/${key}`,
        { value: String(value) }
      );
      return response.data;
    });
  }

  /**
   * Update multiple settings in batch
   * @param {Array} settings - Array of {section, key, value}
   * @returns {Promise<Array>} Array of updated settings
   *
   * @example
   * await client.updateSettingsBatch([
   *   { section: 'payment', key: 'currency', value: 'GBP' },
   *   { section: 'payment', key: 'tax_rate_uk', value: '0.20' }
   * ]);
   */
  async updateSettingsBatch(settings) {
    if (!Array.isArray(settings)) {
      throw new Error('Settings must be an array');
    }

    if (settings.length === 0) {
      return [];
    }

    // Validate all settings before making requests
    for (const setting of settings) {
      if (!setting.section || !setting.key) {
        throw new Error('Each setting must have section and key');
      }
      if (setting.value === undefined || setting.value === null) {
        throw new Error(`Setting ${setting.section}.${setting.key} has no value`);
      }
    }

    // Execute all updates in parallel
    const promises = settings.map(({ section, key, value }) =>
      this.updateSetting(section, key, value)
        .then(result => ({ status: 'fulfilled', value: result }))
        .catch(error => ({ status: 'rejected', reason: error, setting: { section, key, value } }))
    );

    const results = await Promise.all(promises);

    // Check for failures
    const failures = results.filter(r => r.status === 'rejected');

    if (failures.length > 0) {
      console.error(
        `[Settings API] ${failures.length} of ${settings.length} settings failed to update:`,
        failures
      );

      const failedSettings = failures.map(f => f.setting);
      const error = new Error(
        `Failed to update ${failures.length} setting(s): ${failedSettings.map(s => `${s.section}.${s.key}`).join(', ')}`
      );
      error.failures = failures;
      error.partialSuccess = results.filter(r => r.status === 'fulfilled').map(r => r.value);

      throw error;
    }

    return results.map(r => r.value);
  }

  /**
   * Delete a setting
   * @param {string} section - Section name
   * @param {string} key - Setting key
   * @returns {Promise<void>}
   */
  async deleteSetting(section, key) {
    if (!section || !key) {
      throw new Error('Section and key are required');
    }

    return retryWithBackoff(async () => {
      await api.delete(`/api/settings/${section}/${key}`);
    });
  }

  /**
   * Get all available sections
   * @returns {Promise<Array<string>>} Array of section names
   */
  async getAllSections() {
    return retryWithBackoff(async () => {
      const response = await api.get('/api/settings/sections');
      return response.data;
    });
  }

  /**
   * Get all settings (flat array)
   * @returns {Promise<Array>} Array of all settings
   */
  async getAllSettings() {
    return retryWithBackoff(async () => {
      const response = await api.get('/api/settings');
      return response.data;
    });
  }

  /**
   * Check if Settings API is available
   * @returns {Promise<boolean>} True if API is reachable
   */
  async healthCheck() {
    try {
      await this.getAllSections();
      return true;
    } catch (error) {
      console.error('[Settings API] Health check failed:', error.message);
      return false;
    }
  }

  /**
   * Bulk delete settings
   * @param {Array} settings - Array of {section, key}
   * @returns {Promise<Array>} Array of deletion results
   */
  async deleteSettingsBatch(settings) {
    if (!Array.isArray(settings)) {
      throw new Error('Settings must be an array');
    }

    const promises = settings.map(({ section, key }) =>
      this.deleteSetting(section, key)
        .then(() => ({ status: 'fulfilled', setting: { section, key } }))
        .catch(error => ({ status: 'rejected', reason: error, setting: { section, key } }))
    );

    const results = await Promise.all(promises);

    const failures = results.filter(r => r.status === 'rejected');
    if (failures.length > 0) {
      console.warn(`[Settings API] ${failures.length} deletions failed:`, failures);
    }

    return results;
  }
}

// Create singleton instance
const settingsApiClient = new SettingsApiClient();

// Export both the instance and the class
export { SettingsApiClient };
export default settingsApiClient;
